import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showInternalServerErrorMessage } from 'common/ToastMessage/utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useCreateDesign } from 'components/Wizards/DesignWizard/useCreateDesign'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { DESIGN_PATH } from 'paths'
import { useRef } from 'react'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { generatePath, useHistory } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'
import { DesignValues } from '../Types'
import { useModals } from 'almanac-ui/Modal'
import { reportError } from 'utils/error'

type HookOptions = {
  navigateToDesignOnComplete?: boolean
  loadingToast: string
}

export const useMobileWizardCreateDesign = (channel: DesignChannel, options?: HookOptions) => {
  const { navigateToDesignOnComplete = true, loadingToast } = options ?? {}
  const creatingRef = useRef(false)
  const { drawersStore } = useRootStore()
  const { closeAllModals } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const defaultFolder = useDefaultFolder()
  const { currentFolder } = useCurrentFolderStore()
  const history = useHistory()
  const { mutateAsync } = useCreateDesign({ workspaceId })

  const createDesign = async (params: DesignValues) => {
    const {
      width,
      height,
      initial_media_file_id,
      initial_media_file_remove_background,
      filename,
      project,
      source_visual_element_id,
      brand_visual_kit_id,
      cross_post_accounts,
      topic,
      brand_voice_id,
      tone,
      language,
      caption,
      brand_kit_id,
      content,
      archetype,
    } = params

    // Prevent double submit
    if (creatingRef.current) {
      return
    }

    creatingRef.current = true

    const generatingToast = showToastMessage(loadingToast, 'loading', undefined, 0)

    try {
      const seed = {
        apply_brand: false,
      }

      const firstAccount = cross_post_accounts?.find((account) => account.provider === providerFromChannelType[channel])
      const otherAccounts = cross_post_accounts?.filter(
        (account) => account.external_integration_id !== firstAccount?.external_integration_id,
      )

      const projectId = project?.id || currentFolder?.id || defaultFolder?.id || ''

      const design = await mutateAsync({
        width,
        height,
        topic,
        brand_voice_id,
        tone,
        language,
        source_visual_element_id,
        title: filename,
        parent_id: projectId,
        channel,
        initial_media_file_id,
        initial_media_file_remove_background,
        external_integration_id: firstAccount?.external_integration_id,
        brand_visual_kit_id,
        captions: caption ? [caption] : undefined,
        selected_caption_id: caption ? 0 : undefined,
        cross_post_accounts: otherAccounts || [],
        seed,
        brand_kit_id,
        content,
        archetype,
        completed_actions: {
          template: Boolean(source_visual_element_id),
          generate_caption: Boolean(topic),
          core_image: Boolean(initial_media_file_id),
          apply_brand: seed.apply_brand,
        },
      })

      if (!design) {
        throw new Error(`Failed to generate design with params: ${JSON.stringify(params)}`)
      }

      if (navigateToDesignOnComplete) {
        drawersStore.closeAllDrawers()
        closeAllModals()
        history.push(generatePath(DESIGN_PATH, { key: design.id }))
      }

      return design
    } catch (error) {
      reportError(error)
      showInternalServerErrorMessage()
      return null
    } finally {
      generatingToast()
      creatingRef.current = false
    }
  }

  return {
    isLoading: creatingRef.current,
    createDesign,
  }
}
